import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ExternalLink,
  Search,
  RefreshCw,
  AlertTriangle,
  Check,
  Copy,
  CheckCircle2,
  Truck,
  Package,
  Navigation,
  MapPin,
  Calendar,
  Clock,
} from 'lucide-react';
import { fetchPublicTracking, type PublicTrackingResponse } from '../api.js';

export function PublicTrackingPage(): React.ReactElement {
  const { code: urlCode } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(urlCode ?? '');
  const [activeCode, setActiveCode] = useState<string | null>(urlCode?.trim() || null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (urlCode && urlCode.trim() !== activeCode) {
      setSearchInput(urlCode.trim());
      setActiveCode(urlCode.trim());
    }
  }, [urlCode]);

  const trackingQuery = useQuery({
    queryKey: ['public-tracking', activeCode],
    queryFn: () => (activeCode ? fetchPublicTracking(activeCode) : null),
    enabled: Boolean(activeCode),
    retry: 1,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = searchInput.trim().replace(/^#/, '');
    if (!clean) return;
    setActiveCode(clean);
    navigate(`/suivi/${encodeURIComponent(clean)}`, { replace: true });
  };

  const copyCode = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const data: PublicTrackingResponse | null | undefined = trackingQuery.data;

  // Milestone Stepper calculation
  const getStepStatus = (stepIndex: number): 'completed' | 'current' | 'upcoming' => {
    if (!data) return 'upcoming';
    if (data.isCancelled || data.isReturn) {
      if (stepIndex === 0) return 'completed';
      if (stepIndex === 1) return 'completed';
      return 'upcoming';
    }
    if (data.isDelivered) return 'completed';

    // 0: Préparée, 1: En transit, 2: En cours de livraison, 3: Livré
    switch (data.status) {
      case 'preparing':
        return stepIndex === 0 ? 'current' : 'upcoming';
      case 'in_transit':
        return stepIndex < 1 ? 'completed' : stepIndex === 1 ? 'current' : 'upcoming';
      case 'out_for_delivery':
        return stepIndex < 2 ? 'completed' : stepIndex === 2 ? 'current' : 'upcoming';
      case 'delivered':
        return 'completed';
      default:
        return stepIndex === 0 ? 'completed' : 'upcoming';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans selection:bg-emerald-100 selection:text-emerald-900">
      {/* Top Header */}
      <header className="bg-white border-b border-slate-200/80 sticky top-0 z-20 shadow-xs">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center text-white shadow-xs">
              <span className="font-bold text-lg leading-none">M</span>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-base tracking-tight text-slate-900">MaPara Tunisie</span>
                <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-emerald-200">
                  Suivi Colis
                </span>
              </div>
              <p className="text-[11px] text-slate-500">Service de livraison & traçabilité en direct</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/shipping"
              className="text-xs font-semibold text-slate-700 hover:text-emerald-700 flex items-center gap-1 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-slate-100 border border-slate-200"
            >
              <span>← Console</span>
            </Link>
            <a
              href="https://www.maparatunisie.tn"
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-slate-600 hover:text-emerald-700 flex items-center gap-1 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-slate-100"
            >
              <span>Boutique MaPara</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8">
        {/* Search Hero */}
        <div className="text-center space-y-3 max-w-xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Suivre l'acheminement de votre commande
          </h1>
          <p className="text-sm text-slate-600">
            Saisissez votre numéro de code-barres (ex: <code>842480253191</code>), votre commande ou votre <strong>numéro de téléphone</strong> (ex: <code>29212846</code>).
          </p>

          <form onSubmit={handleSearch} className="relative mt-4 flex items-center shadow-sm rounded-2xl bg-white border border-slate-200 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/20 transition-all">
            <Search className="h-5 w-5 text-slate-400 absolute left-4 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="N° de suivi (842480253191), commande (#12345) ou tél (29212846)…"
              className="w-full pl-12 pr-28 py-3.5 bg-transparent text-sm sm:text-base text-slate-900 placeholder:text-slate-400 focus:outline-none font-medium"
            />
            <button
              type="submit"
              disabled={trackingQuery.isFetching}
              className="absolute right-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-semibold transition-colors flex items-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50"
            >
              {trackingQuery.isFetching ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                'Rechercher'
              )}
            </button>
          </form>
        </div>

        {/* Loading State */}
        {trackingQuery.isFetching && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center space-y-3 shadow-xs">
            <RefreshCw className="h-8 w-8 text-emerald-600 animate-spin mx-auto" />
            <p className="text-sm font-semibold text-slate-700">Interrogation du réseau de transport en direct…</p>
            <p className="text-xs text-slate-500">Recherche dans les hubs logistiques et tournées des coursiers</p>
          </div>
        )}

        {/* Error / Not Found State */}
        {trackingQuery.isError && (
          <div className="bg-white rounded-2xl border border-rose-200 p-8 text-center space-y-4 shadow-xs">
            <div className="h-12 w-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Colis introuvable</h3>
              <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
                Aucun colis n'a été trouvé pour la référence « {activeCode} ».
                Veuillez vérifier les chiffres ou contacter notre service client si la commande vient d'être passée.
              </p>
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="text-xs font-semibold text-emerald-700 hover:underline"
              >
                Essayer avec un autre numéro
              </button>
            </div>
          </div>
        )}

        {/* Success: Parcel Tracking Result */}
        {data && !trackingQuery.isFetching && (
          <div className="space-y-6">
            {/* Multi-order picker or status summary if customer has multiple parcels */}
            {data.relatedParcels && data.relatedParcels.length > 1 && (
              <div className="bg-emerald-50 border border-emerald-200/90 rounded-2xl p-4 space-y-2 shadow-2xs">
                <div className="text-xs font-bold text-emerald-950 flex items-center gap-1.5">
                  <Package className="h-4 w-4 text-emerald-700" />
                  Commandes récentes associées ({data.relatedParcels.length}) :
                </div>
                {data.searchedByPhone && (
                  <p className="text-[11px] text-emerald-800/80">
                    Détail de votre commande la plus récente affiché ci-dessous. Pour ouvrir le suivi complet d&apos;une autre commande, saisissez son numéro de suivi direct.
                  </p>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  {data.relatedParcels.map((p, idx) => {
                    const isCurrent = p.isCurrent ?? (p.codeBarre ? p.codeBarre === data.codeBarre : idx === 0);
                    if (p.codeBarre) {
                      return (
                        <button
                          key={p.codeBarre}
                          type="button"
                          onClick={() => {
                            setSearchInput(p.codeBarre!);
                            setActiveCode(p.codeBarre!);
                            navigate(`/suivi/${encodeURIComponent(p.codeBarre!)}`, { replace: true });
                          }}
                          className={`px-3 py-1.5 rounded-xl border text-xs font-medium transition-all cursor-pointer flex items-center gap-2 ${
                            isCurrent
                              ? 'bg-emerald-700 text-white border-emerald-800 font-bold shadow-xs'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50'
                          }`}
                        >
                          <span className="font-mono">{p.codeBarre}</span>
                          {p.facture && <span className="opacity-80">#{p.facture}</span>}
                          <span
                            className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${
                              isCurrent ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {p.etatLabel}
                          </span>
                        </button>
                      );
                    }
                    return (
                      <div
                        key={idx}
                        className={`px-3 py-1.5 rounded-xl border text-xs font-medium flex items-center gap-2 ${
                          isCurrent
                            ? 'bg-emerald-700 text-white border-emerald-800 font-bold shadow-xs'
                            : 'bg-white text-slate-700 border-slate-200'
                        }`}
                      >
                        <span>{p.dateAdd ? `Commande du ${p.dateAdd}` : `Commande #${idx + 1}`}</span>
                        <span
                          className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${
                            isCurrent ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {p.etatLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Status Hero Card */}
            <div className="bg-white rounded-2xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100">
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Suivi N°
                    </span>
                    <span className="font-mono text-lg sm:text-xl font-bold text-slate-900">
                      {data.codeBarre}
                    </span>
                    {!data.searchedByPhone && (
                      <button
                        type="button"
                        onClick={() => copyCode(data.codeBarre)}
                        className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        title="Copier le code"
                      >
                        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    )}
                    {data.orderNumber && (
                      <span className="bg-slate-100 text-slate-700 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-slate-200">
                        Commande {data.orderNumber}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Transporteur partenaire : <strong className="text-slate-800">{data.carrier}</strong>
                  </p>
                </div>

                {/* Status Badge */}
                <div className="shrink-0">
                  {data.isDelivered ? (
                    <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-900 border border-emerald-300">
                      <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                      Colis Livré
                    </span>
                  ) : data.isReturn ? (
                    <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300">
                      <AlertTriangle className="h-4 w-4 text-amber-700" />
                      {data.statusLabel}
                    </span>
                  ) : data.isCancelled ? (
                    <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold bg-rose-100 text-rose-900 border border-rose-300">
                      <AlertTriangle className="h-4 w-4 text-rose-700" />
                      Commande Annulée
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold bg-sky-100 text-sky-900 border border-sky-300">
                      <Truck className="h-4 w-4 text-sky-700 animate-pulse" />
                      {data.statusLabel}
                    </span>
                  )}
                </div>
              </div>

              {/* Progress Milestones Stepper */}
              <div className="py-2">
                <div className="grid grid-cols-4 gap-2 sm:gap-4 relative">
                  {/* Connecting bar */}
                  <div className="absolute top-4 left-[12%] right-[12%] h-1 bg-slate-100 -z-0">
                    <div
                      className="h-1 bg-emerald-600 transition-all duration-500"
                      style={{
                        width: data.isDelivered
                          ? '100%'
                          : data.status === 'out_for_delivery'
                            ? '66%'
                            : data.status === 'in_transit'
                              ? '33%'
                              : '0%',
                      }}
                    />
                  </div>

                  {/* Step 1: Préparé */}
                  <div className="flex flex-col items-center text-center relative z-10">
                    <div
                      className={`h-9 w-9 rounded-full flex items-center justify-center font-bold text-xs transition-colors ${
                        getStepStatus(0) === 'completed'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'bg-white border-2 border-slate-300 text-slate-500'
                      }`}
                    >
                      <Package className="h-4 w-4" />
                    </div>
                    <span className="text-[11px] sm:text-xs font-bold text-slate-900 mt-2">
                      Préparé
                    </span>
                    <span className="text-[10px] text-slate-500 hidden sm:inline">
                      Entrepôt MaPara
                    </span>
                  </div>

                  {/* Step 2: En transit */}
                  <div className="flex flex-col items-center text-center relative z-10">
                    <div
                      className={`h-9 w-9 rounded-full flex items-center justify-center font-bold text-xs transition-colors ${
                        getStepStatus(1) === 'completed'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : getStepStatus(1) === 'current'
                            ? 'bg-sky-600 text-white shadow-xs animate-pulse'
                            : 'bg-white border-2 border-slate-300 text-slate-500'
                      }`}
                    >
                      <Truck className="h-4 w-4" />
                    </div>
                    <span className="text-[11px] sm:text-xs font-bold text-slate-900 mt-2">
                      En acheminement
                    </span>
                    <span className="text-[10px] text-slate-500 hidden sm:inline">
                      Hub transporteur
                    </span>
                  </div>

                  {/* Step 3: En cours de livraison */}
                  <div className="flex flex-col items-center text-center relative z-10">
                    <div
                      className={`h-9 w-9 rounded-full flex items-center justify-center font-bold text-xs transition-colors ${
                        getStepStatus(2) === 'completed'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : getStepStatus(2) === 'current'
                            ? 'bg-sky-600 text-white shadow-xs animate-pulse'
                            : 'bg-white border-2 border-slate-300 text-slate-500'
                      }`}
                    >
                      <Navigation className="h-4 w-4" />
                    </div>
                    <span className="text-[11px] sm:text-xs font-bold text-slate-900 mt-2">
                      En livraison
                    </span>
                    <span className="text-[10px] text-slate-500 hidden sm:inline">
                      Tournée coursier
                    </span>
                  </div>

                  {/* Step 4: Livré */}
                  <div className="flex flex-col items-center text-center relative z-10">
                    <div
                      className={`h-9 w-9 rounded-full flex items-center justify-center font-bold text-xs transition-colors ${
                        getStepStatus(3) === 'completed'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'bg-white border-2 border-slate-300 text-slate-500'
                      }`}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <span className="text-[11px] sm:text-xs font-bold text-slate-900 mt-2">
                      Livré
                    </span>
                    <span className="text-[10px] text-slate-500 hidden sm:inline">
                      Remis au client
                    </span>
                  </div>
                </div>
              </div>

              {/* Destination & Recipient pill */}
              <div className="bg-slate-50 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4 text-xs">
                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin className="h-4 w-4 text-slate-400 shrink-0" />
                  <span>
                    Destination : <strong className="text-slate-900">{data.destination}</strong>
                  </span>
                </div>
                {data.recipientName && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <span className="text-slate-400">Destinataire :</span>
                    <strong className="text-slate-900">{data.recipientName}</strong>
                  </div>
                )}
                {data.dispatchedAt && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
                    <span>
                      Expédié le :{' '}
                      <strong className="text-slate-900">
                        {new Date(data.dispatchedAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </strong>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Events Timeline */}
            <div className="bg-white rounded-2xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-6">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-500" />
                Historique des étapes de livraison
              </h3>

              <div className="relative pl-6 space-y-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                {data.events.map((ev, idx) => (
                  <div key={idx} className="relative group">
                    <div
                      className={`absolute -left-6 top-1 h-3.5 w-3.5 rounded-full border-2 border-white ${
                        idx === 0 ? 'bg-emerald-600 ring-4 ring-emerald-100' : 'bg-slate-400'
                      }`}
                    />
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h4
                          className={`text-sm font-bold ${
                            idx === 0 ? 'text-slate-900' : 'text-slate-700'
                          }`}
                        >
                          {ev.title}
                        </h4>
                        <span className="text-[11px] font-mono text-slate-500">
                          {new Date(ev.date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      {ev.description && (
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {ev.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-6 mt-12 text-center text-xs text-slate-500 space-y-1">
        <p>© {new Date().getFullYear()} MaPara Tunisie. Tous droits réservés.</p>
        <p>Plateforme logistique officielle et suivi de livraison en direct.</p>
      </footer>
    </div>
  );
}
